if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.isSyncingThumbnailScroll = false;
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        // Keep thumbnail active state in sync immediately with main media changes.
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        const thumbnailNavButtons = Array.from(
          this.elements.thumbnails.querySelectorAll('button[name="previous"], button[name="next"]')
        ).map((button) => {
          const replacementButton = button.cloneNode(true);
          button.replaceWith(replacementButton);
          return replacementButton;
        });
        thumbnailNavButtons.forEach((button) => {
          button.addEventListener('click', this.onThumbnailNavClick.bind(this));
        });
        this.elements.thumbnails.addEventListener('slideChanged', this.onThumbnailSlideChanged.bind(this));
        this.elements.thumbnails.enableSliderLooping = true;
        thumbnailNavButtons.forEach((button) => button.removeAttribute('disabled'));
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onThumbnailSlideChanged(event) {
        if (this.isSyncingThumbnailScroll) return;
        const targetMediaId = event.detail.currentElement?.dataset?.target;
        if (!targetMediaId) return;
        this.setActiveMedia(targetMediaId, false);
      }

      onThumbnailNavClick(event) {
        const direction = event.currentTarget?.name;
        if (!direction) return;
        event.preventDefault();
        this.shiftActiveThumbnail(direction);
      }

      shiftActiveThumbnail(direction) {
        if (!this.elements.thumbnails) return;
        const thumbnailItems = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        if (!thumbnailItems.length) return;

        const activeIndex = thumbnailItems.findIndex((item) => item.querySelector('button[aria-current]'));
        const currentIndex = activeIndex >= 0 ? activeIndex : 0;
        const step = direction === 'next' ? 1 : -1;

        // Move in one direction only; do not wrap to start/end.
        for (
          let nextIndex = currentIndex + step;
          nextIndex >= 0 && nextIndex < thumbnailItems.length;
          nextIndex += step
        ) {
          const nextItem = thumbnailItems[nextIndex];
          const nextMediaId = nextItem?.dataset?.target;
          if (!nextMediaId) continue;
          const mediaExists = this.elements.viewer.querySelector(`[data-media-id="${nextMediaId}"]`);
          if (!mediaExists) continue;
          this.setActiveMedia(nextMediaId, false);
          return;
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.isSyncingThumbnailScroll = true;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        window.setTimeout(() => {
          this.isSyncingThumbnailScroll = false;
        }, 120);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
